import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import moment from 'moment';

import { GlobalContext } from '../../../../store';
import { IGame } from '../../../../store/player/games/store';
import { ISeason } from '../../../../store/seasons/store';
import { IEventItem } from '../../../../store/player/events/store';

import { dateFormat } from '../../../../contrib/utils';
import { CurrentEvent } from '../CurrentEvent/CurrentEvent';
import { ArrowLeft } from './ArrowLeft';
import { ArrowRight } from './ArrowRight';
import { today } from '../../../../Utils/today';
import { IComponentsLabelMap } from '../../../common/Utils/IComponentsLabelMap';
import { isDateUCTSameAsToday } from '../../../../Utils/isDateUCTSameAsToday';

interface IProps {
  clubId: number;
  currentSeason?: ISeason;
}

const EventToday: FC<IProps> = props => {
  const { clubId, currentSeason } = props;

  const { events, games } = useContext(GlobalContext);

  const [activeTab, setActiveTab] = useState(0);
  const [leaveTab, setLeaveTab] = useState(0);
  const [direction, setDirection] = useState('');
  const [isCurrentSeason, setIsCurrentSeason] = useState(false);
  const [displayLeftArrow, setDisplayLeftArrow] = useState(false);
  const [displayRightArrow, setDisplayRightArrow] = useState(false);

  const [allEvents, setAllEvents] = useState<IEventItem[]>([]);
  const [todayGames, setTodayGames] = useState<IGame[]>([]);

  const [currentGames, setCurrentGames] = useState<IComponentsLabelMap[]>([]);

  const setTab = useCallback(
    (activeTabNum: number) => {
      const leaveTabNum = activeTab;

      if (leaveTabNum !== activeTabNum) {
        setActiveTab(activeTabNum);
        setLeaveTab(leaveTabNum);
        setDirection(leaveTabNum < activeTabNum ? 'ltr' : 'rtl');
      }
    },
    [activeTab]
  );

  const goToPreviousTab = useCallback(() => setTab(activeTab - 1), [activeTab]);
  const goToNextTab = useCallback(() => setTab(activeTab + 1), [activeTab]);

  useEffect(() => {
    setAllEvents(events && events.all && Object.values(events.all));
  }, [events]);

  useEffect(() => {
    if (Object.values(games).length > 0) {
      const newTodayGames = Object.values(games).filter((game: IGame) =>
        isDateUCTSameAsToday(game.group.start)
      );

      setTodayGames(newTodayGames);
    }
  }, [games]);

  useEffect(() => {
    const todayEventsIds = todayGames.map(e => e.round.event);

    const clubEventsIds = allEvents
      .filter(e => Number(clubId) === e.club.id && todayEventsIds.includes(e.id))
      .map(e => e.id);

    const clubTodayGames = todayGames.filter(todayGame => {
      const todayGameEventId = todayGame.round.event;

      return (
        events &&
        events.all[todayGameEventId] &&
        events.all[todayGameEventId].club.id === Number(clubId) &&
        clubEventsIds.includes(todayGameEventId)
      );
    });

    const newCurrentGames = clubTodayGames.map((currentGame: IGame, index: number) => ({
      content: <CurrentEvent currentGame={currentGame} />,
      label: `CURRENT EVENT ${index}`
    }));

    setCurrentGames(newCurrentGames);
  }, [todayGames, allEvents, clubId, events]);

  useEffect(() => {
    if (currentSeason) {
      const currentSeasonStartDate = moment(currentSeason.start, dateFormat);
      const currentSeasonEndDate = moment(currentSeason.end, dateFormat);

      setIsCurrentSeason(today.isBetween(currentSeasonStartDate, currentSeasonEndDate));
    }
    setIsCurrentSeason(true);
  }, [currentSeason]);

  useEffect(() => {
    setDisplayLeftArrow(currentGames.length > 1 && activeTab > 0);
  }, [currentGames, activeTab]);

  useEffect(() => {
    setDisplayRightArrow(
      currentGames.length > 1 &&
        activeTab < currentGames.length &&
        activeTab + 1 !== currentGames.length
    );
  }, [currentGames, activeTab]);

  return isCurrentSeason && currentGames.length > 0 ? (
    <div className="event-today-wrapper">
      <div className={`event-today override`}>
        <div className="top-bar">
          {displayLeftArrow && <ArrowLeft onClick={goToPreviousTab} />}

          <p>EVENT TODAY!</p>

          {displayRightArrow && <ArrowRight onClick={goToNextTab} />}
        </div>

        <div className={`slider ${direction} admin-layout`}>
          {currentGames.map(({ content }, index) => (
            <div
              key={`club-settings-step-${index}`}
              className={`slide top admin-wrapper ${activeTab === index ? 'active' : ''} ${
                leaveTab === index ? 'leave' : ''
              }`}
            >
              {content}
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default EventToday;
